import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, DataTable, catcherAccuracyScore, columnTypes as colType } from 'yankees-react-utils';
import { useOktaQueries, Player, Stats } from '../../../data';
import ReportCard from '../../../components/ReportCard';
import VideoPlayerButton from '../../../components/VideoPlayerButton';

const StyledReportCard = styled(ReportCard)`
    margin-top: 10px;
`;
const StyledSuperHeader = styled.tr`
    > td {
        background-color: #244C74;
        color: #FFF;
    }
`;

const buildQueries = (playerid, yearid, levelid) => {
    const startyear = yearid;
    const endyear = yearid;
    const queries = [
        {
            queryKey: ['player', playerid],
            queryFn: (t) => Player.get(t, { playerId: playerid }),
        },
        {
            queryKey: ['catcherThrows', playerid],
            queryFn: (t) => Stats.catcherThrows(t, { playerid }),
        },
        {
            queryKey: ['catcherCharts', playerid, startyear, endyear, levelid],
            queryFn: (t) => Stats.catcherCharts(t, { playerid, startyear, endyear, levelid }),
        },
    ];
    return queries;
};

const mapDataResponse = (response, gameid) => {
    const player = response[0].data || {};
    const throwData = response[1].data || {};
    const catcherCharts = response[2].data || {};
    const catcherThrows2B = throwData.catcherthrows.filter((t) => t.gameid === gameid && t.base === 2).map((r) => {
        const videoparams = { gameid: r.gameid, eventseq: r.eventseq, pitchseq: r.pitchseq };
        const videoplaylist = (<VideoPlayerButton {...videoparams} />);
        return { ...r, videoplaylist };
    });
    const catcherThrows3B = throwData.catcherthrows.filter((t) => t.gameid === gameid && t.base === 3).map((r) => {
        const videoparams = { gameid: r.gameid, eventseq: r.eventseq, pitchseq: r.pitchseq };
        const videoplaylist = (<VideoPlayerButton {...videoparams} />);
        return { ...r, videoplaylist };
    });
    const catcherThrowPK = throwData.catcherpickoffthrows.filter((t) => t.gameid === gameid).map((r) => {
        const videoparams = { gameid: r.gameid, eventseq: r.eventseq, pitchseq: r.pitchseq };
        const videoplaylist = (<VideoPlayerButton {...videoparams} />);
        return { ...r, videoplaylist };
    });
    const { mlb: lgAvg } = catcherCharts;

    return { player, catcherThrows2B, catcherThrows3B, catcherThrowPK, lgAvg };
};

const throwType = (translate, row) => {
    let type = '--';
    if (row.isthrow) {
        if (row.goodthrow + row.greatthrow === 2) {
            type = translate('sections.post-game.throwing.great');
        } else if (row.goodthrow + row.greatthrow === 1) {
            type = translate('sections.post-game.throwing.good');
        } else {
            type = translate('sections.post-game.throwing.poor');
        }
    }
    return type;
};

const yesNo = (translate, row, key) => {
    if (!([key] in row)) return '--';
    return !row[key] ? translate('common.no') : translate('common.yes');
};

const columnsSb = (translate) => [
    { id: 'videoplaylist', title: null, type: colType.NODE },
    { id: 'inning', title: translate('baseball.inning'), transform: (row) => row.inning },
    { id: 'pitcher', title: translate('baseball.pitcher_one'), transform: (row) => row.pitchername, type: colType.TEXT },
    { id: 'runner', title: translate('baseball.runners_one'), transform: (row) => row.runnername, type: colType.TEXT },
    { id: 'isthrow', title: translate('sections.post-game.throwing.throw'), transform: (row) => yesNo(translate, row, 'isthrow'), type: colType.TEXT },
    { id: 'velocity', title: translate('sections.post-game.throwing.velocity'), transform: (row) => row.throwvelo, precision: 1 },
    { id: 'poptime', title: translate('sections.post-game.throwing.pop-time'), transform: (row) => row.catchertime, precision: 2 },
    { id: 'throwtime', title: translate('sections.post-game.throwing.throw-time'), transform: (row) => row.throwtime, precision: 2 },
    { id: 'exchange', title: translate('sections.post-game.throwing.exchange'), transform: (row) => row.transfertime, precision: 2 },
    { id: 'accuracy', title: translate('sections.post-game.throwing.accuracy'), transform: (row) => throwType(translate, row), type: colType.TEXT },
    { id: 'bounced', title: translate('sections.post-game.throwing.bounced'), transform: (row) => yesNo(translate, row, 'isbounced'), type: colType.TEXT },
    { id: 'result', title: translate('sections.post-game.throwing.result'), transform: (row) => row.type, type: colType.TEXT },
    { id: 'error', title: translate('sections.post-game.throwing.throwing-error'), transform: (row) => yesNo(translate, row, 'te'), type: colType.TEXT },
];

const columnsSbLgAvg = (translate) => [
    { id: 'title', title: null, transform: () => 'League Avg', type: colType.TEXT },
    { id: 'velocity', title: translate('sections.post-game.throwing.velocity'), transform: (row) => row.throwvelo, precision: 1 },
    { id: 'poptime', title: translate('sections.post-game.throwing.pop-time'), transform: (row) => row.catchertime, precision: 2 },
    { id: 'throwtime', title: translate('sections.post-game.throwing.throw-time'), transform: (row) => row.throwtime, precision: 2 },
    { id: 'exchange', title: translate('sections.post-game.throwing.exchange'), transform: (row) => row.transfertime, precision: 2 },
    { id: 'accuracy', title: translate('sections.post-game.throwing.accuracy'), transform: (row) => catcherAccuracyScore(row.throws, row.greatthrows, row.goodthrows, 2), type: colType.TEXT },
];

const columnsPk = (translate) => [
    { id: 'videoplaylist', title: null, type: colType.NODE },
    { id: 'inning', title: translate('baseball.inning'), transform: (row) => row.inning },
    { id: 'pitcher', title: translate('baseball.pitcher_one'), transform: (row) => row.pitchername, type: colType.TEXT },
    { id: 'runner', title: translate('baseball.runners_one'), transform: (row) => row.runnername, type: colType.TEXT },
    { id: 'base', title: translate('sections.post-game.throwing.base'), transform: (row) => row.base },
    { id: 'result', title: translate('sections.post-game.throwing.result'), transform: (row) => row.type, type: colType.TEXT },
    { id: 'error', title: translate('sections.post-game.throwing.throwing-error'), transform: (row) => (row.te ? 'Yes' : 'No'), type: colType.TEXT },
];

const Throwing = ({ playerid, gameid, gameinfo }) => {
    const { t: translate } = useTranslation();
    const { yearid, levelid } = gameinfo;
    const dataQueries = buildQueries(playerid, yearid, levelid);
    const dataQueriesResponse = useOktaQueries(dataQueries);

    if (dataQueriesResponse.some((q) => q.isLoading)) {
        return <LoadingSpinner />;
    }

    const { catcherThrows2B, catcherThrows3B, catcherThrowPK, lgAvg } = mapDataResponse(dataQueriesResponse, gameid);
    const lgAvg2B = lgAvg.filter((l) => l.base === 2);
    const lgAvg3B = lgAvg.filter((l) => l.base === 3);

    const lgAvgSuperheader = (
        <StyledSuperHeader>
            <td colSpan={6}>{translate('sections.post-game.throwing.abbr-league-average')}</td>
        </StyledSuperHeader>
    );

    return (
        <>
            <StyledReportCard title={translate('sections.post-game.throwing.catcher-throws-2b')}>
                {catcherThrows2B.length > 0 ? (
                    <>
                        <DataTable columns={columnsSb(translate)} rows={catcherThrows2B} />
                        <DataTable columns={columnsSbLgAvg(translate)} rows={lgAvg2B} superheader={lgAvgSuperheader} />
                    </>
                ) : (<span>{translate('sections.post-game.throwing.no-catcher-throws')}</span>)}
            </StyledReportCard>
            <StyledReportCard title={translate('sections.post-game.throwing.catcher-throws-3b')}>
                {catcherThrows3B.length > 0 ? (
                    <>
                        <DataTable columns={columnsSb(translate)} rows={catcherThrows3B} />
                        <DataTable columns={columnsSbLgAvg(translate)} rows={lgAvg3B} superheader={lgAvgSuperheader} />
                    </>
                ) : (<span>{translate('sections.post-game.throwing.no-catcher-throws')}</span>)}
            </StyledReportCard>
            <StyledReportCard title={translate('sections.post-game.throwing.catcher-backpicks')}>
                {catcherThrowPK.length > 0 ? <DataTable columns={columnsPk(translate)} rows={catcherThrowPK} /> : (<span>{translate('sections.post-game.throwing.no-pickoff-throws')}</span>)}
            </StyledReportCard>
        </>
    );
};

Throwing.propTypes = {
    playerid: PropTypes.number.isRequired,
    gameid: PropTypes.number.isRequired,
    gameinfo: PropTypes.shape({
        gameid: PropTypes.number,
        gamedate: PropTypes.string,
        yearid: PropTypes.number,
        levelid: PropTypes.number,
    }).isRequired,
};

export default Throwing;
